import React, {
  Fragment,
  useCallback,
  useMemo,
  useState
} from 'react';
import i18n from 'i18next';
import productStyles from './productNativeForm.module.scss';
import imageStyles from './imageForm.module.scss';
import imageInputStyles from 'components/Image/imageInput.module.scss';
import {
  RichMediaFormProps,
  RichMediaType,
  RichMediaDeliveryPosition,
  MARQUEE_IMAGE_COUNT,
  RICH_MEDIA_MARQUEE_VALID_TYPES,
  getMarqueeImageMediaKey,
  getMarqueeImageSize,
  isMarqueeRichMediaType
} from './RichMediaFormModel';
import { connect, FormikContextType } from 'formik';
import { FormConfig } from 'components/form/FormConfig';
import { FormContent } from 'components/form/Form';
import { get, partial } from 'lodash';
import { ProductHome } from 'containers/RetailRMN/Products/ProductHome';
import { ProductListDataContext } from 'containers/RetailRMN/Products/ProductListDataContext';
import Tags from 'components/Tags/Tags';
import { SelectOptions } from 'components/commonType';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { FormikField } from 'components/form/field/FormikField';
import { useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';

export const RichMediaForm: React.FunctionComponent<
  RichMediaFormProps & { formik: FormikContextType<any> }
> = ({
  basicFields,
  renderHintModal,
  getInitFormConfig,
  ...props
}) => {
  const { values, setFieldValue, validateField } = props.formik;
  const advertiserId = values.advertiserId;
  const richMediaType: RichMediaType = get(
    values,
    'typeProperties.richMediaType',
    RichMediaType.IMAGE_IMAGE
  );
  const deliveryPosition: RichMediaDeliveryPosition = get(
    values,
    'typeProperties.deliveryPosition',
    RichMediaDeliveryPosition.IN_SITE
  );
  const location = useLocation();
  const defaultFilters = useMemo(
    () =>
      get(location.state, 'filters', {
        advertiser: advertiserId
      }),
    [advertiserId, location.state]
  );
  const [formConfig, setFormConfig] = useState<FormConfig>(getInitFormConfig());

  const { model } = props;
  const { flowPageModel, allowOffSite } = model;
  const {
    validateBarImage,
    validateExpandImage,
    validateVideo,
    validateMarqueeImages,
    validateLandingPageUrl,
    validateProducts,
    barImageFormModel,
    expandImageFormModel,
    videoValidTypes
  } = model;
  const {
    products,
    filterSet,
    handleRemoveProduct,
    handleProductsButtonClick,
    handleSelectedProducts,
    onModalConfirm,
    onModalCancel,
    handleFilterChanged
  } = model.productNativeFormModel;

  const isVideo = richMediaType === RichMediaType.IMAGE_VIDEO;
  const isMarquee = isMarqueeRichMediaType(richMediaType);
  const marqueeImageSize = getMarqueeImageSize(richMediaType);
  const marqueeImageSizeText = `${marqueeImageSize.width} x ${marqueeImageSize.height}`;

  const richMediaTypeOptions: SelectOptions[] = useMemo(
    () => [
      {
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaTypeImageImage'),
        value: RichMediaType.IMAGE_IMAGE
      },
      {
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaTypeImageVideo'),
        value: RichMediaType.IMAGE_VIDEO
      },
      {
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaTypeMarqueePc'),
        value: RichMediaType.MARQUEE_PC
      },
      {
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaTypeMarqueeMobile'),
        value: RichMediaType.MARQUEE_MOBILE
      }
    ],
    []
  );

  const deliveryPositionOptions: SelectOptions[] = useMemo(() => {
    const options = [
      {
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaInSite'),
        value: RichMediaDeliveryPosition.IN_SITE
      }
    ];
    if (allowOffSite) {
      options.push({
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaOffSite'),
        value: RichMediaDeliveryPosition.OFF_SITE
      });
    }
    return options;
  }, [allowOffSite]);

  // 切換版型時把另一組版型的圖片欄位清掉，避免帶著上一個版型的素材（尺寸限制不同）送出
  const onRichMediaTypeChange = useCallback(
    (value) => {
      model.setRichMediaType(value);
      setFieldValue('medias.expandImage', undefined);
      setFieldValue('medias.expandVideo', undefined);
      if (isMarqueeRichMediaType(value)) {
        // 跑馬燈不用置底圖；桌機／行動裝置互換時尺寸限制不同，三張圖也要重新上傳
        setFieldValue('medias.barImage', undefined);
      }
      for (let index = 0; index < MARQUEE_IMAGE_COUNT; index++) {
        setFieldValue(`medias.${getMarqueeImageMediaKey(index)}`, undefined);
      }
    },
    [model, setFieldValue]
  );

  // 導外連結（站內限 pchome.com.tw 網域）與商品（站內必填）的驗證規則都取決於投遞位置，
  // 但 withFormikField 是在 formik 跑完那一輪驗證後才用 setTimeout 呼叫這個 callback，
  // 也就是 formik 驗證時 model 的投遞位置還是舊值。這裡要重新驗證，過期的錯誤才會被清掉（或補上）。
  const onDeliveryPositionChange = useCallback(
    (value) => {
      model.setDeliveryPosition(value);
      validateField('bannerUrl');
      validateField('selectProducts');
    },
    [model, validateField]
  );

  const renderButtons = useCallback(() => {
    return (
      <>
        <Button variant='primary' size='sm' onClick={onModalConfirm}>
          {i18n.t<string>('common.buttons.confirm')}
        </Button>
        <Button variant='secondary' size='sm' onClick={onModalCancel}>
          {i18n.t<string>('common.buttons.cancel')}
        </Button>
      </>
    );
  }, [onModalConfirm, onModalCancel]);

  const productListModalData = useMemo(() => {
    return {
      render: () => (
        <ProductListDataContext.Provider
          value={{
            selectedProducts: products,
            handleSelectedProducts,
            handleFilterChanged
          }}
        >
          <ProductHome
            defaultFilters={filterSet || defaultFilters}
            renderButtons={renderButtons}
            singleSelect
          />
        </ProductListDataContext.Provider>
      ),
      props: {
        title: '',
        dismiss: onModalCancel,
        className: productStyles.productListModal
      }
    };
  }, [
    filterSet,
    defaultFilters,
    products,
    handleSelectedProducts,
    handleFilterChanged,
    onModalCancel,
    renderButtons
  ]);

  const renderProductsField = useCallback(() => {
    const onModalOpen = partial(handleProductsButtonClick, productListModalData);
    const productsOptions: SelectOptions[] = products.map((product) => ({
      label: product.name,
      value: product.productId.toString()
    }));
    const renderFieldContent = () => (
      <Fragment>
        {products.length > 0 ? (
          <div className={productStyles.tagsContainer}>
            <Tags
              value={productsOptions}
              disableInput
              onChange={handleRemoveProduct}
              className={productStyles.tags}
            />
            <div className={productStyles.editIcon}>
              <IconWithTooltip
                icon={faPencilAlt}
                tooltipProps={{
                  id: `RichMediaFormEditProducts`,
                  tooltip: i18n.t<string>('creativeSetupFlow.labels.editProductsHint')
                }}
                onClick={onModalOpen}
              />
            </div>
          </div>
        ) : (
          <div className={productStyles.selectProductsRow}>
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={onModalOpen}
            >
              {i18n.t<string>('creativeSetupFlow.labels.selectProducts')}
            </button>
            <FormikField.Label
              isFlexibleContent={true}
              fieldContentWidth={20}
              inputColSm={3}
              name='selectProducts'
              validate={validateProducts}
            />
          </div>
        )}
      </Fragment>
    );
    return (
      <Fragment>{!flowPageModel.isModalOpen && renderFieldContent()}</Fragment>
    );
  }, [
    flowPageModel.isModalOpen,
    products,
    productListModalData,
    handleProductsButtonClick,
    handleRemoveProduct,
    validateProducts
  ]);

  React.useEffect(() => {
    setFieldValue('typeProperties.products', products);
    const barImageHint = (
      <div>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.imageTypeHint')}
        </span>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.storageHint', { storage: '1MB' })}
        </span>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.imageSizeHint', { size: '600 x 100' })}
        </span>
      </div>
    );
    const marqueeImageHint = (
      <div>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.richMediaMarqueeImageTypeHint')}
        </span>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.storageHint', { storage: '1MB' })}
        </span>
        <span className={imageStyles.hint}>
          {i18n.t<string>('creativeSetupFlow.labels.imageSizeHint', { size: marqueeImageSizeText })}
        </span>
      </div>
    );

    const fieldsBuilder = new FormConfig.FieldsBuilder([...basicFields])
      .addFormikSelect({
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaType'),
        name: 'typeProperties.richMediaType',
        simpleValue: true,
        options: richMediaTypeOptions,
        onChange: onRichMediaTypeChange
      })
      .addFormikSelect({
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaDeliveryPosition'),
        name: 'typeProperties.deliveryPosition',
        simpleValue: true,
        options: deliveryPositionOptions,
        onChange: onDeliveryPositionChange
      });

    if (isMarquee) {
      // 跑馬燈：三個固定順序的圖片欄位取代置底圖 + 主素材，陣列順序即輪播順序（圖片1 → 圖片2 → 圖片3）
      for (let index = 0; index < MARQUEE_IMAGE_COUNT; index++) {
        fieldsBuilder.addFormikFileInput({
          type: 'image',
          className: imageInputStyles.fitImageHeight,
          label: i18n.t<string>(`creativeSetupFlow.labels.richMediaMarqueeImage${index + 1}`),
          name: `medias.${getMarqueeImageMediaKey(index)}`,
          validTypes: RICH_MEDIA_MARQUEE_VALID_TYPES,
          fieldContentWidth: 318,
          hints: [],
          permanentHint: marqueeImageHint,
          validate: validateMarqueeImages[index]
        });
      }
    } else {
      fieldsBuilder.addFormikFileInput({
        type: 'image',
        className: imageInputStyles.fitImageHeight,
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaBarImage'),
        name: 'medias.barImage',
        validTypes: barImageFormModel.validTypes,
        fieldContentWidth: 318,
        hints: [],
        permanentHint: barImageHint,
        validate: validateBarImage
      });
    }

    if (!isMarquee && isVideo) {
      fieldsBuilder.addFormikFileInput({
        type: 'video',
        className: imageStyles.imageInput,
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaExpandVideo'),
        name: 'medias.expandVideo',
        validTypes: videoValidTypes,
        fieldContentWidth: 318,
        hints: [],
        permanentHint: (
          <div>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.videoTypeHint')}
            </span>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.storageHint', { storage: '5MB' })}
            </span>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.videoRatioHint', { ratio: '16:9' })}
            </span>
          </div>
        ),
        validate: validateVideo
      });
    } else if (!isMarquee) {
      fieldsBuilder.addFormikFileInput({
        type: 'image',
        className: imageStyles.imageInput,
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaExpandImage'),
        name: 'medias.expandImage',
        validTypes: expandImageFormModel.validTypes,
        fieldContentWidth: 318,
        hints: [],
        permanentHint: (
          <div>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.imageTypeHint')}
            </span>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.storageHint', { storage: '1MB' })}
            </span>
            <span className={imageStyles.hint}>
              {i18n.t<string>('creativeSetupFlow.labels.imageSizeHint', { size: '600 x 300' })}
            </span>
          </div>
        ),
        validate: validateExpandImage
      });
    }

    fieldsBuilder
      .addFormikUrlInput({
        label: i18n.t<string>('creativeSetupFlow.labels.richMediaLandingPageUrl'),
        name: 'bannerUrl',
        fieldContentWidth: 424,
        validate: validateLandingPageUrl
      })
      .addFormikCustom({
        name: 'typeProperties.products',
        label: i18n.t<string>('creativeSetupFlow.labels.products'),
        render: renderProductsField,
        formGroupClassName: productStyles.productsFieldContainer
      });

    setFormConfig(
      new FormConfig.Builder()
        .addSection(
          new FormConfig.SectionBuilder(fieldsBuilder.build())
            .withTitle(i18n.t<string>('creativeSetupFlow.labels.creativeBasicInfo'))
            .withHintModal(renderHintModal())
            .build()
        )
        .build()
    );
  }, [
    flowPageModel.type,
    isVideo,
    isMarquee,
    marqueeImageSizeText,
    deliveryPosition,
    products,
    basicFields,
    barImageFormModel,
    expandImageFormModel,
    videoValidTypes,
    richMediaTypeOptions,
    deliveryPositionOptions,
    onRichMediaTypeChange,
    onDeliveryPositionChange,
    validateBarImage,
    validateExpandImage,
    validateVideo,
    validateMarqueeImages,
    validateLandingPageUrl,
    setFieldValue,
    renderHintModal,
    renderProductsField
  ]);

  return <FormContent formConfig={formConfig} />;
};

export default connect(RichMediaForm);
